/**
 * OnboardingActivationWizard — Week 1 Launch Prep checklist.
 *
 * A modal dialog that auto-shows once for new Pro/Studio users when they
 * first open the Loom with a project loaded. It guides them through 5
 * activation steps (manuscript → audit → synopsis → plot blueprint → score).
 *
 * Each step's CTA dispatches a window event that opens the relevant tool.
 * Completion is auto-detected from the backend — no manual "mark done" needed.
 * Dismiss state is persisted per-user via the backend.
 */
import React, { useEffect, useState, useCallback } from 'react';
import {
  CheckCircle2, Circle, ChevronRight, X, Sparkles, Loader2,
  BookOpen, Search, FileText, GitBranch, BarChart3,
} from 'lucide-react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { cn } from '@/lib/utils';
import { onboardingApi } from '@/lib/api';
import { useAuth } from '@/lib/auth_context';
import { usePW } from '@/lib/store';

const STEP_ICONS = {
  manuscript_connected:  BookOpen,
  drift_audit_run:       Search,
  synopsis_generated:    FileText,
  plot_blueprint_built:  GitBranch,
  sales_readiness_scored: BarChart3,
};

const STEP_ACTIONS = {
  manuscript_connected:   null,   // navigate to chapters
  drift_audit_run:        'loom:open-continuity-audit',
  synopsis_generated:     'loom:open-manuscript-synopsis',
  plot_blueprint_built:   'loom:open-plot-builder',
  sales_readiness_scored: 'loom:open-plot-builder',   // same dialog, score section
};

const ALLOWED_TIERS = new Set(['pro', 'pro_plus', 'premier', 'author', 'studio']);

// Persist "wizard seen" locally so we don't flash it on every page load
// before the backend dismiss check completes.
function getLocalSeen(pid) {
  try { return window.localStorage.getItem(`cr_onboard_seen_${pid}`) === '1'; } catch { return false; }
}
function setLocalSeen(pid) {
  try { window.localStorage.setItem(`cr_onboard_seen_${pid}`, '1'); } catch { /* ignore */ }
}

export default function OnboardingActivationWizard() {
  const { user } = useAuth();
  const { project } = usePW();

  const [open, setOpen]       = useState(false);
  const [steps, setSteps]     = useState([]);
  const [completed, setCompleted] = useState(0);
  const [total, setTotal]     = useState(5);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  const pid = project?.id;

  // Decide whether to show
  const shouldShow = useCallback(() => {
    if (!user || !pid) return false;
    const tier = (user.subscription_tier || '').toLowerCase();
    const isAdmin = user.is_admin || user.role === 'admin';
    // Show to admin (for testing) or any paid tier user
    return isAdmin || ALLOWED_TIERS.has(tier);
  }, [user, pid]);

  const loadChecklist = useCallback(async (silent = false) => {
    if (!pid) return;
    if (!silent) setLoading(true);
    else setRefreshing(true);
    try {
      const data = await onboardingApi.getChecklist(pid);
      if (!data.show_checklist) { setOpen(false); return; }
      setSteps(data.steps || []);
      setCompleted(data.completed_count || 0);
      setTotal(data.total || 5);
      return data;
    } catch (e) {
      // silently ignore — wizard is non-critical
    } finally {
      if (!silent) setLoading(false);
      else setRefreshing(false);
    }
  }, [pid]);

  // Auto-show on first project load
  useEffect(() => {
    if (!shouldShow() || !pid) return;
    // If dismissed locally, don't flash
    if (getLocalSeen(pid)) return;

    // Small delay so the Loom finishes mounting
    const timer = setTimeout(async () => {
      const data = await loadChecklist();
      // Don't show if all steps complete or dismissed via backend
      if (!data) return;
      if (data.all_complete) return;
      // Check backend dismiss flag — it's in the user record, not returned
      // by the checklist endpoint. We use local storage as a proxy: if the
      // user dismissed, we set local storage. Re-show never happens locally.
      setOpen(true);
    }, 1200);
    return () => clearTimeout(timer);
  }, [pid, shouldShow, loadChecklist]);

  const handleDismiss = async () => {
    setOpen(false);
    setLocalSeen(pid);
    try { await onboardingApi.dismiss(pid); } catch { /* ignore */ }
  };

  const handleStepCta = async (step) => {
    const eventName = STEP_ACTIONS[step.id];
    setOpen(false);
    setLocalSeen(pid);
    if (eventName) {
      window.dispatchEvent(new CustomEvent(eventName));
    }
  };

  const handleRefresh = async () => {
    await loadChecklist(true);
  };

  const pct = total > 0 ? Math.round((completed / total) * 100) : 0;
  const allDone = completed >= total;

  if (!open) return null;

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) handleDismiss(); }}>
      <DialogContent
        overlayClassName="bg-transparent pointer-events-none"
        className="max-w-lg"
        data-testid="onboarding-wizard-dialog"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-base">
            <Sparkles className="h-4 w-4 text-amber-500" />
            Week 1 Launch Prep
          </DialogTitle>
          <DialogDescription className="text-[12.5px]">
            Five steps to get your manuscript market-ready. Complete them in order for the best results.
          </DialogDescription>
        </DialogHeader>

        {/* Progress bar */}
        <div className="space-y-1.5">
          <div className="flex items-center justify-between text-[11.5px]">
            <span className="text-muted-foreground">{completed} of {total} complete</span>
            <span className={cn('font-semibold tabular-nums', allDone ? 'text-emerald-600' : 'text-primary')}>
              {pct}%
            </span>
          </div>
          <Progress value={pct} className="h-1.5" data-testid="onboarding-progress-bar" />
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-8 gap-2 text-muted-foreground text-[13px]">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading checklist…
          </div>
        ) : allDone ? (
          <div className="rounded-lg bg-emerald-50 border border-emerald-200 px-4 py-5 text-center space-y-2 dark:bg-emerald-950/30 dark:border-emerald-800">
            <CheckCircle2 className="mx-auto h-8 w-8 text-emerald-500" />
            <p className="font-semibold text-[13.5px] text-emerald-700 dark:text-emerald-400">
              All 5 steps complete — your manuscript is launch-ready!
            </p>
            <p className="text-[12px] text-muted-foreground">
              Check your Sales Readiness Score in the Plot Builder to see where you stand against the 85% threshold.
            </p>
          </div>
        ) : (
          <div className="space-y-2" data-testid="onboarding-steps-list">
            {steps.map((step, idx) => {
              const Icon = STEP_ICONS[step.id] || Circle;
              const isLocked = idx > 0 && !steps[idx - 1]?.completed;
              return (
                <div
                  key={step.id}
                  data-testid={`onboarding-step-${step.id}`}
                  className={cn(
                    'flex items-start gap-3 rounded-lg border p-3 transition-colors',
                    step.completed
                      ? 'border-emerald-200 bg-emerald-50/50 dark:border-emerald-800 dark:bg-emerald-950/20'
                      : isLocked
                        ? 'border-border bg-muted/30 opacity-60'
                        : 'border-border bg-background hover:bg-muted/30',
                  )}
                >
                  <div className="mt-0.5 flex-shrink-0">
                    {step.completed
                      ? <CheckCircle2 className="h-4.5 w-4.5 text-emerald-500" />
                      : <Circle className={cn('h-4.5 w-4.5', isLocked ? 'text-muted-foreground/40' : 'text-muted-foreground')} />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5">
                      <Icon className={cn('h-3.5 w-3.5 flex-shrink-0', step.completed ? 'text-emerald-500' : 'text-primary')} />
                      <p className={cn('text-[12.5px] font-semibold', step.completed && 'line-through text-muted-foreground')}>
                        {step.label}
                      </p>
                    </div>
                    <p className="text-[11.5px] text-muted-foreground mt-0.5 leading-snug">
                      {step.description}
                    </p>
                  </div>
                  {!step.completed && !isLocked && STEP_ACTIONS[step.id] && (
                    <Button
                      size="sm"
                      variant="outline"
                      className="flex-shrink-0 h-7 text-[11.5px] gap-1"
                      onClick={() => handleStepCta(step)}
                      data-testid={`onboarding-step-cta-${step.id}`}
                    >
                      {step.cta}
                      <ChevronRight className="h-3 w-3" />
                    </Button>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* Footer */}
        <div className="flex items-center justify-between pt-1">
          <button
            type="button"
            onClick={handleRefresh}
            disabled={refreshing}
            className="flex items-center gap-1 text-[11.5px] text-muted-foreground hover:text-foreground transition-colors"
            data-testid="onboarding-refresh-btn"
          >
            {refreshing
              ? <Loader2 className="h-3 w-3 animate-spin" />
              : <span>↻</span>}
            Refresh progress
          </button>
          <button
            type="button"
            onClick={handleDismiss}
            className="flex items-center gap-1 text-[11.5px] text-muted-foreground hover:text-foreground transition-colors"
            data-testid="onboarding-dismiss-btn"
          >
            <X className="h-3 w-3" /> Dismiss
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
